/**
 * The session, in one place.
 *
 * Tokens are moving to httpOnly cookies. A cookie the browser sends on its own
 * is a cookie no script can read, which is the point — an XSS that reaches
 * `localStorage` walks away with a working session, and the same XSS against a
 * cookie it cannot read gets nothing. But it also means that once the switch is
 * made, nothing in this app can build an Authorization header, check whether a
 * token exists, or put one in a URL.
 *
 * About a hundred files used to do exactly those things, each reading storage
 * itself. They now come through here, so the switch is this file rather than a
 * hundred files: flip `COOKIE_SESSION`, and every caller is already correct.
 *
 * Until the server actually sets those cookies, the Bearer path stays live —
 * stripping the headers first would only mean a day where the frontend sends
 * nothing and the backend still expects a token.
 */

/**
 * Which world we are in.
 *
 * `false` — the server returns tokens in the login body; they live in storage
 *           and travel in an Authorization header.
 * `true`  — the server sets httpOnly cookies; the browser carries them, this
 *           file stops touching storage, and headers go out empty.
 *
 * Flip this the day the backend ships. Nothing else needs to change.
 */
export const COOKIE_SESSION = true;

/* Kept as it has always been. Over a hundred files were reading this key, and
   renaming it would have been churn with no gain. In cookie mode it is unused. */
const ACCESS_KEY = "cms_token";
const REFRESH_KEY = "cms_refresh_token";

/**
 * A readable marker cookie the server sets beside the httpOnly pair.
 *
 * The session cookies themselves cannot be read, so something has to tell this
 * app whether it is signed in before it makes its first request. That is all
 * this is — it carries no credential, and forging it gains nothing: every real
 * request is still authorised by the httpOnly cookie the forger does not have.
 */
const MARKER_COOKIE = "cms_logged_in";

const readCookie = (name) => {
  const hit = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : "";
};

/**
 * The Authorization header for a call to our own API.
 *
 * Empty in cookie mode — the browser is already carrying the credential, and an
 * empty object spreads into a header block without leaving a trace, so no caller
 * needs to know which mode it is in. It is also empty when there is no session
 * at all: `Bearer ` with nothing after it is a malformed header, not an
 * unauthenticated one, and the two get different answers from a server.
 */
export const authHeaders = () => {
  if (COOKIE_SESSION) return {};
  const token = localStorage.getItem(ACCESS_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Options for a `fetch` to our own API.
 *
 * Axios has one global switch for this; `fetch` does not — it drops cookies on a
 * cross-site request unless each call asks for them, and the CMS is served from
 * a different host to its API. A call that forgets gets a 401 with no hint as to
 * why, so the asking happens here rather than at each site.
 */
export const fetchInit = (init = {}) => ({
  ...init,
  credentials: "include",
  headers: { ...(init.headers || {}), ...authHeaders() },
});

/**
 * The raw token, for the few places a header will not do.
 *
 * An `EventSource` cannot set headers, so the SSE endpoint takes its credential
 * in the query string — the one case that genuinely needs the string itself. In
 * cookie mode there is nothing to hand back, and those callers switch to
 * `withCredentials` instead; everything else should be using `authHeaders()`.
 */
export const getSessionToken = () =>
  (COOKIE_SESSION ? "" : localStorage.getItem(ACCESS_KEY) || "");

/** Whether to show the app at all. Never used to authorise anything. */
export const hasSession = () =>
  (COOKIE_SESSION ? readCookie(MARKER_COOKIE) === "1" : Boolean(localStorage.getItem(ACCESS_KEY)));

/**
 * Store what a login or refresh returned.
 *
 * In cookie mode the response body carries no tokens — the server set them in
 * `Set-Cookie` headers before this code ever ran — so there is nothing to do.
 * The legacy `token` field is still accepted so a server not yet issuing refresh
 * tokens keeps working.
 *
 * @returns {boolean} whether a usable session is now in place.
 */
export const setSession = (payload = {}) => {
  if (COOKIE_SESSION) return true;

  const body = payload.data && typeof payload.data === "object" ? payload.data : payload;
  const access = body.access_token || body.token || "";
  const refresh = body.refresh_token || "";

  if (access) localStorage.setItem(ACCESS_KEY, access);
  /* Only overwritten when a new one actually arrived: a response without a
     refresh token must not wipe the one already held. */
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);

  return Boolean(access);
};

/** What a refresh call has to send. Nothing, once the cookie carries it. */
export const refreshPayload = () =>
  (COOKIE_SESSION ? {} : { refresh_token: localStorage.getItem(REFRESH_KEY) || "" });

/**
 * Whether a refresh is even worth attempting.
 *
 * Always, in cookie mode. The refresh cookie is httpOnly — this app cannot see
 * whether it exists, and the marker cookie is not proof either way: a marker can
 * be missing while the session is perfectly fine (the server never set it, a
 * browser dropped it). Guessing "no" here turns a refreshable request into a
 * logout. The server is the only thing that actually knows, so ask it.
 */
export const canRefresh = () =>
  (COOKIE_SESSION ? true : Boolean(localStorage.getItem(REFRESH_KEY)));

/**
 * Forget the session here.
 *
 * Only the server can clear an httpOnly cookie, so in cookie mode this drops the
 * marker and leaves the rest to the logout call — which is why logging out must
 * reach the server rather than just clearing storage.
 */
export const clearSession = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  document.cookie = `${MARKER_COOKIE}=; Max-Age=0; path=/`;
};

/**
 * Sweep out tokens left over from before the switch.
 *
 * A browser that was signed in the day this flipped still holds the old pair.
 * Nothing reads them any more — but a real credential sitting in storage is the
 * precise thing this change exists to stop, and "unread" is not "gone". Runs
 * once, on the first import, and is a no-op in Bearer mode where those same
 * values are the live session.
 */
if (COOKIE_SESSION) {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}
